import {IEvent} from "@webng-types/write-model";
import {isViafouraCommentConfiguration} from "@webng/liveblog-edit-utils";
import {commentsEnabled} from "../converter/commentsEnabled";
import {CommentsMode} from "../LiveblogRenderContext";

/**
 * Whether a liveblog needs the shared Viafoura overlay mounted at all.
 *
 * Extracted so it can be tested and so the coupling is explicit: this MUST agree with the per-post
 * gate in `LiveblogEventRow`. When it was merely `_type === Viafoura`, a live update disabling a post
 * — or a theme switching to `commentsMode: "disabled"` — removed the trigger while leaving an open
 * conversation live behind it, because the overlay stayed mounted. Dropping out of this predicate
 * unmounts the sidebar, and its cleanup runs the shared close path, so the widget is torn down.
 *
 * `enableComments` is deliberately NOT part of this: that prop suppresses conversations on individual
 * Tickaroo-owned surfaces (share pages, event references), which render rows without a Liveblog root.
 */
export function needsViafouraOverlay(
  rows: Pick<IEvent, "comments">[],
  commentsMode: CommentsMode
): boolean {
  return eligibleViafouraContainerIds(rows, commentsMode).length > 0;
}

/**
 * The `local_id`s of the posts that currently render a Viafoura trigger.
 *
 * `needsViafouraOverlay` only answers "is *any* post eligible", which leaves a gap: when the post
 * whose conversation is **open** stops being eligible — filtered away, paginated out, or disabled by
 * a live update — while another eligible post remains, the aggregate stays true, the overlay stays
 * mounted, and it keeps showing a conversation whose trigger is gone. Comparing the open id against
 * this list is what closes that.
 */
export function eligibleViafouraContainerIds(
  rows: Pick<IEvent, "comments" | "local_id">[],
  commentsMode: CommentsMode
): string[] {
  // Allowlist, exactly as in LiveblogEventRow: only the modes that mean "show it" mount the overlay,
  // so an unrecognised or legacy value cannot leave a hidden overlay behind rows that render no
  // trigger. My first version denylisted "disabled" and drifted from the row gate on the very next
  // value — the test below is what caught it.
  if (commentsMode !== "default" && commentsMode !== "inline") {
    return [];
  }
  return rows
    .filter(row => isViafouraCommentConfiguration(row.comments) && commentsEnabled(row.comments))
    .map(row => row.local_id)
    .filter((id): id is string => !!id);
}
